import React, { useRef, useEffect } from "react";
import FilePreview from "~/components/FilePreview";

function DropzoneFile({
                          label = "Assign Thumbnail(*)",
                          register,
                          name = "file",
                          errors,
                          selectedFile,
                          setSelectedFile,
                          maxSizeMB = 2,
                          acceptedFormats = ["image/jpeg", "image/png", "image/jpg"],
                          disabled = false,
                          showPreview = true,
                          requiredMessage = "Thumbnail is required."
                      }) {
    const fileInputRef = useRef();

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
        }

        const newFile = {
            name: file.name,
            size: (file.size / 1024).toFixed(2) + " KB",
            createdDate: new Date().toISOString(),
            preview: URL.createObjectURL(file),
            file,
        };

        setSelectedFile(newFile);
    };

    const handleRemoveFile = () => {
        if (selectedFile?.preview) URL.revokeObjectURL(selectedFile.preview);
        setSelectedFile(null);
        fileInputRef.current.value = "";
    };

    useEffect(() => {
        return () => {
            if (selectedFile?.preview) URL.revokeObjectURL(selectedFile.preview);
        };
    }, [selectedFile]);

    useEffect(() => {
        if (selectedFile?.file && fileInputRef.current) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(selectedFile.file);
            fileInputRef.current.files = dataTransfer.files;

            register(name).onChange({ target: fileInputRef.current });
        }
    }, [selectedFile]);

    return (
        <div className="mb-0">
            <h6 className="mb-2">{label}</h6>
            <div
                role="presentation"
                tabIndex="0"
                className="dropzone pt-3 pb-3"
                onClick={!disabled ? handleBrowseClick : undefined}
            >
                <input
                    disabled={disabled}
                    tabIndex="-1"
                    type="file"
                    {...(register && register(name, {
                        required: requiredMessage,
                        validate: {
                            lessThanMaxSize: (files) =>
                                files[0]?.size < maxSizeMB * 1024 * 1024 ||
                                `File must be smaller than ${maxSizeMB}MB.`,
                            acceptedFormats: (files) =>
                                acceptedFormats.includes(files[0]?.type) ||
                                `Only ${acceptedFormats
                                    .map((f) => f.split("/")[1].toUpperCase())
                                    .join("/")} files are allowed.`,
                        },
                    }))}
                    ref={(el) => {
                        register  && register(name).ref(el);
                        fileInputRef.current = el;
                    }}
                    onChange={(e) => {
                        handleFileChange(e);
                        register  && register(name).onChange(e);
                    }}
                />

                <div className="text-body-tertiary text-opacity-85 fw-bold fs-9">
                    Drag your files here{" "}
                    <span className="text-body-secondary">or </span>
                    <button disabled={disabled} type="button" className="p-0 btn btn-link">
                        Browse from device
                    </button>
                    <br />
                    <img
                        className="mt-3"
                        width="40"
                        alt=""
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACsAAAAqCAMAAAAd31JXAAAARVBMVEUAAABueJFueJFueZFteJBweI9seJFtepJweI9ueJFwcI9ueJFueZFteZJvd5Bud5FueJFue5FuepFueZBueJBtd5BueJFT8Zn8AAAAFnRSTlMA339fn2B/H0C/EO9vP49Pr08v78+Pw4dpogAAARZJREFUOMvF1NluwyAQheFhiQl4jdPO+z9qQdgcSmjHyk3+C8uWPuGRF0jOeb/SxRTzGC5ajrm37DzYl0yeNZ4lu1k7p+tgudeUZz06Zv7ibjYty1Ur0cD9TLOucvlyXHTbPc87aZ3goqdIZ/GRADzS8nTN3kTrC5CtW6yTLfq4DaG1YdZ67dGB+fu3XZ8cU+bVjs3zxVcxdN/FHRY0YsFm6n2FZ+v94lp7pon0iZ3HTWoLWrBTmKi2oMDPavzaggKn1E1nXFnQA+OXMZyDBQXOrwoYVgECD8CNFXBtJVxbAXdsH4sWWLbAsgXOtuw7o/sHb+mwE53bpppML+y2oWxBYpZy+yhSVWZ8KInuVHLGq7/bpnC4H8tmOEloDtVDAAAAAElFTkSuQmCC"
                    />
                </div>
            </div>

            {errors && errors[name] && (
                <span className="fs-9 fw-bold text-danger">{errors[name].message}</span>
            )}

            {selectedFile && showPreview && (
                <FilePreview
                    src={selectedFile.preview}
                    fileName={selectedFile.name}
                    fileSize={selectedFile.size}
                    handleRemoveClick={handleRemoveFile}
                    disabled={disabled}
                />
            )}
        </div>
    );
}

export default DropzoneFile;
